// Shared phone normalisation for the whole SPA (operator + public), kept in lockstep with the server's
// IdentityNormalization. Used by the public booking form (public/validation.ts) and the Clientes search
// (customers.ts), so search, validation and storage all fold a number the same way.

// '+' only as the first character or right after a leading '(' ("(+238) ..."); preCleanPhone drops any "(0)"
// trunk after a country code first.
const PHONE_CHARS = /^\(?\+?[\d ()\-.]+$/;

// Invisible format/bidi marks dropped before the "(0)" rule, in lockstep with the server's
// IdentityNormalization.IgnorableFormatChars (kept byte-for-byte): NEL, LRM/RLM, the bidi embedding/override
// and isolate sets, and the zero-width/format invisibles (ZWSP/ZWNJ/ZWJ, word joiner, soft hyphen, Arabic
// letter mark, ZWNBSP/BOM). A pasted zero-width between a country code and "(0)" would otherwise push the trunk
// off the anchor; NEL is DROPPED (not mapped to a space) to match the server's public StripUnsafe path, so a
// leading NEL never becomes a stray leading space that fails validation. Exported so the strip test enumerates
// the SAME list the code strips (no drift).
export const IGNORABLE_CODE_POINTS: readonly number[] = [
  0x0085, // NEL
  0x00ad, // soft hyphen
  0x061c, // Arabic letter mark
  0x200b, 0x200c, 0x200d, // ZWSP, ZWNJ, ZWJ
  0x200e, 0x200f, // LRM, RLM
  0x202a, 0x202b, 0x202c, 0x202d, 0x202e, // bidi embeddings/overrides
  0x2060, // word joiner
  0x2066, 0x2067, 0x2068, 0x2069, // bidi isolates
  0xfeff // ZWNBSP / BOM
];
const IGNORABLE = new Set<number>(IGNORABLE_CODE_POINTS);

// Map any Unicode whitespace to a plain space, drop directional marks (LRM/RLM and the bidi override/isolate
// set the API's StripUnsafe also removes), and drop a "(0)" trunk anchored at the START right after a + or 00
// country code (any ( ) - . or space separators between). A bare "(0)" with no country code is left alone.
// Anchored so it is linear (no ReDoS); callers also cap the input length before calling this.
export function preCleanPhone(phone: string): string {
  let out = '';
  for (const c of phone) {
    const cp = c.codePointAt(0) ?? 0;
    if (IGNORABLE.has(cp)) {
      continue;
    }
    if (/\s/u.test(c)) {
      out += ' '; // any whitespace (tab/newline/NBSP) -> a plain space
    } else if (!(cp < 0x20 || cp === 0x7f || (cp >= 0x80 && cp <= 0x9f))) {
      out += c; // other C0/C1/DEL control chars are dropped (mirrors the server)
    }
  }
  return out.replace(/^([ (]*(?:\+|00)[ ()\-.]*\d{1,3}[ ()\-.]*)\(0\)/, '$1');
}

export function isValidPhone(phone: string): boolean {
  const cleaned = preCleanPhone(phone);
  if (!PHONE_CHARS.test(cleaned)) {
    return false;
  }
  const digits = (cleaned.match(/\d/g) ?? []).length;
  return digits >= 6 && digits <= 15;
}

// The dialable/storable form the API keeps: leading '+' if present, digits, single spaces; punctuation dropped.
export function normalizePhone(phone: string): string {
  let out = '';
  let lastWasSpace = false;
  for (const c of preCleanPhone(phone)) {
    if (/\d/.test(c) || (c === '+' && out.length === 0)) {
      out += c;
      lastWasSpace = false;
    } else if (c === ' ' && out.length > 0 && !lastWasSpace) {
      out += ' ';
      lastWasSpace = true;
    }
  }
  return out.trimEnd();
}
